<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import {
  listNotifications,
  getUnreadCount,
  markAsRead,
  markAllAsRead,
  type Notification,
} from '@/api/notification'

const router = useRouter()

const unreadCount = ref(0)
const showPanel = ref(false)
const notifications = ref<Notification[]>([])
const loading = ref(false)

let pollTimer: ReturnType<typeof setInterval> | null = null

async function fetchUnreadCount(): Promise<void> {
  try {
    const res = await getUnreadCount()
    unreadCount.value = res.count
  } catch {
    // silently ignore
  }
}

async function fetchNotifications(): Promise<void> {
  loading.value = true
  try {
    const res = await listNotifications({ page: 1, page_size: 10 })
    notifications.value = res.items
  } catch {
    // silently ignore
  } finally {
    loading.value = false
  }
}

async function handleMarkRead(n: Notification): Promise<void> {
  if (!n.is_read) {
    try {
      await markAsRead(n.id)
      n.is_read = true
      unreadCount.value = Math.max(0, unreadCount.value - 1)
    } catch {
      // silently ignore
    }
  }
}

async function handleMarkAllRead(): Promise<void> {
  try {
    await markAllAsRead()
    notifications.value.forEach((n) => { n.is_read = true })
    unreadCount.value = 0
  } catch {
    // silently ignore
  }
}

function togglePanel(): void {
  showPanel.value = !showPanel.value
  if (showPanel.value) {
    fetchNotifications()
  }
}

function viewAll(): void {
  showPanel.value = false
  router.push('/notifications')
}

function handleClickOutside(e: MouseEvent): void {
  const el = (e.target as HTMLElement).closest('.notification-bell')
  if (!el) {
    showPanel.value = false
  }
}

function formatTime(iso: string): string {
  const d = new Date(iso)
  const now = new Date()
  const diffMs = now.getTime() - d.getTime()
  const diffMin = Math.floor(diffMs / 60000)
  if (diffMin < 1) return '刚刚'
  if (diffMin < 60) return `${diffMin}分钟前`
  const diffHour = Math.floor(diffMin / 60)
  if (diffHour < 24) return `${diffHour}小时前`
  const diffDay = Math.floor(diffHour / 24)
  if (diffDay < 30) return `${diffDay}天前`
  return d.toLocaleDateString('zh-CN')
}

onMounted(() => {
  fetchUnreadCount()
  pollTimer = setInterval(fetchUnreadCount, 60000)
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  if (pollTimer) clearInterval(pollTimer)
  document.removeEventListener('click', handleClickOutside)
})
</script>

<template>
  <div class="notification-bell">
    <button class="notification-bell__btn" @click="togglePanel">
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M10 2a5.5 5.5 0 0 0-5.5 5.5V12l-1.5 2h14l-1.5-2V7.5A5.5 5.5 0 0 0 10 2z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
        <path d="M7.5 16a2.5 2.5 0 0 0 5 0" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
      </svg>
      <span v-if="unreadCount > 0" class="notification-bell__badge">
        {{ unreadCount > 99 ? '99+' : unreadCount }}
      </span>
    </button>

    <div v-if="showPanel" class="notification-bell__panel">
      <div class="notification-bell__panel-header">
        <span class="notification-bell__panel-title">消息通知</span>
        <button
          v-if="unreadCount > 0"
          class="notification-bell__mark-all"
          @click="handleMarkAllRead"
        >
          全部已读
        </button>
      </div>

      <div class="notification-bell__panel-body">
        <div v-if="loading" class="notification-bell__empty">加载中...</div>
        <div v-else-if="notifications.length === 0" class="notification-bell__empty">暂无通知</div>
        <template v-else>
          <div
            v-for="n in notifications"
            :key="n.id"
            class="notification-bell__item"
            :class="{ 'notification-bell__item--unread': !n.is_read }"
            @click="handleMarkRead(n)"
          >
            <div class="notification-bell__item-dot" v-if="!n.is_read" />
            <div class="notification-bell__item-content">
              <div class="notification-bell__item-title">{{ n.title }}</div>
              <div v-if="n.body" class="notification-bell__item-body">{{ n.body }}</div>
              <div class="notification-bell__item-time">{{ formatTime(n.created_at) }}</div>
            </div>
          </div>
        </template>
      </div>

      <div class="notification-bell__panel-footer">
        <button class="notification-bell__view-all" @click="viewAll">查看全部</button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.notification-bell {
  position: relative;

  &__btn {
    position: relative;
    padding: var(--aw-space-1) var(--aw-space-2);
    border: none;
    border-radius: var(--aw-radius-sm);
    background: transparent;
    color: var(--aw-text-secondary);
    cursor: pointer;
    transition: all var(--aw-duration-fast);

    &:hover {
      background-color: var(--aw-bg-muted);
      color: var(--aw-text-primary);
    }
  }

  &__badge {
    position: absolute;
    top: 0;
    right: 0;
    min-width: 16px;
    height: 16px;
    padding: 0 4px;
    border-radius: 8px;
    background-color: var(--aw-danger-500);
    color: #fff;
    font-size: 10px;
    line-height: 16px;
    text-align: center;
  }

  &__panel {
    position: absolute;
    top: 100%;
    right: 0;
    width: 360px;
    margin-top: var(--aw-space-2);
    background-color: var(--aw-bg-base);
    border: 1px solid var(--aw-border-default);
    border-radius: var(--aw-radius-lg);
    box-shadow: var(--aw-shadow-lg);
    z-index: 100;
  }

  &__panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--aw-space-3) var(--aw-space-4);
    border-bottom: 1px solid var(--aw-border-default);
  }

  &__panel-title {
    font-size: var(--aw-text-sm);
    font-weight: var(--aw-font-semibold);
    color: var(--aw-text-primary);
  }

  &__mark-all {
    border: none;
    background: transparent;
    color: var(--aw-primary-500);
    font-size: var(--aw-text-xs);
    cursor: pointer;

    &:hover {
      color: var(--aw-primary-600);
    }
  }

  &__panel-body {
    max-height: 400px;
    overflow-y: auto;
  }

  &__empty {
    padding: var(--aw-space-8) var(--aw-space-4);
    text-align: center;
    color: var(--aw-text-tertiary);
    font-size: var(--aw-text-sm);
  }

  &__item {
    display: flex;
    align-items: flex-start;
    gap: var(--aw-space-2);
    padding: var(--aw-space-3) var(--aw-space-4);
    cursor: pointer;
    transition: background-color var(--aw-duration-fast);

    &:hover {
      background-color: var(--aw-bg-subtle);
    }

    &--unread {
      background-color: var(--aw-primary-50, rgba(59, 130, 246, 0.04));
    }
  }

  &__item-dot {
    width: 8px;
    height: 8px;
    margin-top: 6px;
    border-radius: 50%;
    background-color: var(--aw-primary-500);
    flex-shrink: 0;
  }

  &__item-content {
    flex: 1;
    min-width: 0;
  }

  &__item-title {
    font-size: var(--aw-text-sm);
    color: var(--aw-text-primary);
    font-weight: var(--aw-font-medium);
    line-height: 1.4;
  }

  &__item-body {
    margin-top: 2px;
    font-size: var(--aw-text-xs);
    color: var(--aw-text-secondary);
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &__item-time {
    margin-top: 2px;
    font-size: var(--aw-text-xs);
    color: var(--aw-text-tertiary);
  }

  &__panel-footer {
    padding: var(--aw-space-2) var(--aw-space-4);
    border-top: 1px solid var(--aw-border-default);
    text-align: center;
  }

  &__view-all {
    border: none;
    background: transparent;
    color: var(--aw-primary-500);
    font-size: var(--aw-text-sm);
    cursor: pointer;
    width: 100%;
    padding: var(--aw-space-1);

    &:hover {
      color: var(--aw-primary-600);
    }
  }
}
</style>
